import type {ImageItem} from '../../vendor/src/features/remotion-editor/editor/items/image/image-item-type';
import type {EditorState, UndoableState, TrackType} from '../../vendor/src/features/remotion-editor/editor/state/types';
import {editableTree, itemOwner} from './group-split';
import {hasPendingTimelineEdit} from './edit-ownership';
import {editorValueSignature} from '../../protocol/native-envelope';
import {insertTimelineTime} from './timeline-insert';
import {removeTimelineTime} from './timeline-ripple';
import {SOURCE_BRAND_INTRO_SECONDS, SOURCE_BRAND_INTRO_IMAGE, SOURCE_BRAND_INTRO_BG, SOURCE_BRAND_INTRO_FG,
  sourceBrandIntroImageFields, patchSourceBrandIntro, readSourceBrandIntro, validateSourceBrandIntroDocument,
  type SourceBrandIntro, type BrandIntroTextPatch} from '../../protocol/source-brand-intro';
export * from '../../protocol/source-brand-intro';
/** Metadata remains a local intersection until lead wires persistence and renderer dispatch. */
export type SourceBrandIntroItem = ImageItem & { sourceBrandIntro?: SourceBrandIntro };
export type BrandIntroIds = { item: string; asset: string; track: string };
export type SourceBrandIntroPlan = {
  base: UndoableState;
  signature: string;
  next: EditorState;
  action: 'add' | 'update' | 'remove';
  removalMode?: 'ripple' | 'card-only';
  /** UI must show safe fallback; do not describe card-only as time removal. */
  note?: string;
};
export const SOURCE_BRAND_INTRO_BGM_POLICY = 'preserve-global-absolute-window' as const;
const time = (n: number) => Number.isSafeInteger(n) && n >= 0;
const staticTypes = new Set(['image', 'solid', 'text', 'blur', 'pdf', 'ppt']);
export function sourceBrandIntroOfItem(item: unknown): SourceBrandIntro | undefined {
  if (!item || typeof item !== 'object') return undefined;
  return readSourceBrandIntro('sourceBrandIntro' in item ? item.sourceBrandIntro : undefined);
}
export function sourceBrandIntroItems(state: Pick<EditorState, 'undoableState'>): SourceBrandIntroItem[] {
  return Object.values(state.undoableState.items).filter((i): i is SourceBrandIntroItem => {
    const data = sourceBrandIntroOfItem(i);
    if (data && i.type !== 'image') throw Error('오프닝 카드 종류를 확인해 주세요.');
    return i.type === 'image' && !!data;
  });
}
/** Validate complete ownership once; native children already store absolute frames. */
function preflight(state: EditorState, affected: readonly string[]): void {
  if (hasPendingTimelineEdit(state)) throw Error('진행 중인 편집을 먼저 마쳐 주세요.');
  const doc = state.undoableState;
  if (!Number.isFinite(doc.fps) || doc.fps <= 0 || ![doc.compositionWidth, doc.compositionHeight].every(n => Number.isFinite(n) && n > 0 && n <= 8192)) throw Error('영상 크기와 재생 속도를 확인해 주세요.');
  const owned = new Set<string>(), tracks = new Set<string>();
  const visit = (id: string) => {
    const i = doc.items[id];
    if (!i || i.id !== id || owned.has(id) || !time(i.from) || !time(i.durationInFrames) || !i.durationInFrames || !time(i.from + i.durationInFrames)) throw Error('클립의 시간과 소유 관계를 확인해 주세요.');
    owned.add(id);
    if ('assetId' in i && !doc.assets[i.assetId]) throw Error('클립 원본을 확인해 주세요.');
    if (i.type === 'group') {
      if (!i.children.length && !i.preserveGroupFrame) throw Error('빈 그룹을 확인해 주세요.');
      i.children.forEach(visit);
    }
  };
  for (const t of doc.tracks) {
    if (!t.id || tracks.has(t.id)) throw Error('트랙 소유 관계를 확인해 주세요.');
    tracks.add(t.id); t.items.forEach(visit);
  }
  if (owned.size !== Object.keys(doc.items).length || !validateSourceBrandIntroDocument(doc)) throw Error('문서의 클립 소유 관계를 확인해 주세요.');
  for (const t of Object.values(doc.transitions)) {
    if (!tracks.has(t.trackId) || !owned.has(t.sourceItemId) || !owned.has(t.targetItemId) ||
        !time(t.from) || !time(t.durationInFrames) || !t.durationInFrames || !time(t.from + t.durationInFrames) ||
        itemOwner(state, t.sourceItemId)?.track.id !== t.trackId || itemOwner(state, t.targetItemId)?.track.id !== t.trackId) throw Error('전환 효과 구간을 확인해 주세요.');
  }
  for (const id of affected) if (!editableTree(state, id)) throw Error('영향받는 트랙의 잠금을 해제해 주세요.');
}
function persistentRoot(track: TrackType, item: UndoableState['items'][string]): boolean {
  return track.metadata?.trackType === 'brand-template' || (item.timelineSourceItemId ?? item.id).startsWith('waka-story-root-');
}
function persistentRoots(state: EditorState): string[] {
  return state.undoableState.tracks.filter(t => t.metadata?.kind !== 'bgm').flatMap(t => t.items.filter(id => persistentRoot(t, state.undoableState.items[id]!)));
}
function ordinaryRoots(state: EditorState, cardId?: string): string[] {
  return state.undoableState.tracks.filter(t => t.metadata?.kind !== 'bgm').flatMap(t => t.items.filter(id => id !== cardId));
}
/** Persistent brand/root graphics span added time. Never split their assets or source clocks. */
function resizePersistent(state: EditorState, roots: readonly string[], delta: number): EditorState {
  const items = { ...state.undoableState.items };
  const visit = (id: string) => {
    const i = items[id]!;
    if (i.type === 'group') i.children.forEach(visit);
    else if (!staticTypes.has(i.type)) throw Error('상시 브랜드 층의 종류를 확인해 주세요.');
    // Later timed decorations shift; layers starting at zero cover the new opening.
    const next = i.from === 0 ? { ...i, durationInFrames: i.durationInFrames + delta } : { ...i, from: i.from + delta };
    if (!time(next.from) || !time(next.durationInFrames) || !next.durationInFrames) throw Error('상시 브랜드 층의 시간 구간을 확인해 주세요.');
    items[id] = next;
  };
  roots.forEach(visit);
  return { ...state, undoableState: { ...state.undoableState, items } };
}
function layoutSignature(state: EditorState, cardId: string): string {
  const doc = state.undoableState;
  return editorValueSignature({ fps: doc.fps,
    tracks: doc.tracks.map(t => [t.id, t.items.filter(id => id !== cardId), t.metadata?.kind ?? null, t.metadata?.trackType ?? null]),
    items: Object.keys(doc.items).filter(id => id !== cardId).sort().map(id => {
      const i = doc.items[id];
      return [id, i.type, i.from, i.durationInFrames, i.type === 'group' ? [i.children, i.preserveGroupFrame === true] : null];
    }),
    transitions: Object.keys(doc.transitions).sort().map(id => [id, doc.transitions[id]]),
  });
}
function cardSignature(item: SourceBrandIntroItem): string {
  const {sourceBrandIntro, ...native} = item;
  return editorValueSignature([native, sourceBrandIntro?.clock, sourceBrandIntro?.sourceFps]);
}
/** No ordinary content may intersect opening. Preserved groups clip, not offset, children. */
function exclusiveLeading(state: EditorState, card: SourceBrandIntroItem, frames: number): boolean {
  const owner = itemOwner(state, card.id);
  if (!owner || owner.parentId !== null || card.from !== 0 || card.durationInFrames !== frames ||
      Object.values(state.undoableState.transitions).some(t => t.sourceItemId === card.id || t.targetItemId === card.id || (t.from < frames && state.undoableState.tracks.find(x => x.id === t.trackId)?.metadata?.kind !== 'bgm'))) return false;
  const persistent = new Set(persistentRoots(state));
  const visit = (id: string, start = 0, end = Infinity): boolean => {
    const i = state.undoableState.items[id];
    if (!i) return false;
    if (i.type === 'group') {
      if (i.from < frames) return false;
      if (i.preserveGroupFrame) { start = Math.max(start, i.from); end = Math.min(end, i.from + i.durationInFrames); }
      return i.children.every(child => visit(child, start, end));
    }
    const from = Math.max(start, i.from), to = Math.min(end, i.from + i.durationInFrames);
    // Also forbid negative shift of invisible members. Native helpers shift whole trees.
    return i.from >= frames && (to <= from || from >= frames);
  };
  return state.undoableState.tracks.every(t => t.metadata?.kind === 'bgm' || t.items.every(id => id === card.id || persistent.has(id) || visit(id)));
}
/** Lead may bind an imported leading card after full source projection, if proven exclusive. */
export function bindSourceBrandIntroLeadingInterval(state: EditorState, id: string): EditorState {
  preflight(state, []);
  const card = state.undoableState.items[id];
  const data = sourceBrandIntroOfItem(card);
  if (!card || card.type !== 'image' || !data) throw Error('오프닝 카드를 다시 선택해 주세요.');
  const typed: SourceBrandIntroItem = card;
  if(data.provenance.kind==='story'){const persistent=new Set(persistentRoots(state));if(!ordinaryRoots(state,id).some(key=>!persistent.has(key)))return state;}
  if (!exclusiveLeading(state, typed, card.durationInFrames)) return state;
  // Check inverse growth before granting receipt (short persistent layers stay card-only).
  try { resizePersistent(state, persistentRoots(state), -card.durationInFrames); } catch { return state; }
  return { ...state, undoableState: { ...state.undoableState, items: { ...state.undoableState.items,
    [id]: { ...typed, sourceBrandIntro: { ...data, leadingInterval: { frames: card.durationInFrames,
      layout: layoutSignature(state, id), card: cardSignature({ ...typed, sourceBrandIntro: data }) } } },
  } } };
}
export function newSourceBrandIntroItem(state: EditorState, ids: BrandIntroIds, data: SourceBrandIntro, from: number, durationInFrames: number): SourceBrandIntroItem {
  readSourceBrandIntro(data);
  if (!time(from) || !time(durationInFrames) || !durationInFrames) throw Error('오프닝 카드 구간을 확인해 주세요.');
  return sourceBrandIntroImageFields({id:ids.item,assetId:ids.asset,data,from,durationInFrames,width:state.undoableState.compositionWidth,height:state.undoableState.compositionHeight});
}
export function planUpdateSourceBrandIntro(state: EditorState, id: string, patch: BrandIntroTextPatch): SourceBrandIntroPlan {
  preflight(state, [id]);
  const item = state.undoableState.items[id], data = sourceBrandIntroOfItem(item);
  if (!item || item.type !== 'image' || !data) throw Error('오프닝 카드를 다시 선택해 주세요.');
  const nextItem: SourceBrandIntroItem = { ...item, sourceBrandIntro: patchSourceBrandIntro(data, patch) };
  return { base: state.undoableState, signature: editorValueSignature(state.undoableState), action: 'update', next: { ...state, undoableState: { ...state.undoableState,
    items: { ...state.undoableState.items, [id]: nextItem } } } };
}
export function planAddSourceBrandIntro(state: EditorState, text: {title: string; subtitle?: string; bg?: string; fg?: string}, ids: BrandIntroIds): SourceBrandIntroPlan {
  preflight(state, []);
  const cards = sourceBrandIntroItems(state);
  if (cards.length > 1) throw Error('오프닝 카드가 여러 장입니다. 편집할 카드를 선택해 주세요.');
  if (cards.length) return planUpdateSourceBrandIntro(state, cards[0].id, text);
  const doc = state.undoableState;
  if (![ids.item, ids.asset, ids.track].every(id => !!id && id.length <= 512) || doc.items[ids.item] || doc.assets[ids.asset] || doc.tracks.some(t => t.id === ids.track)) throw Error('오프닝 카드 추가 대상을 확인해 주세요.');
  const frames = Math.max(1, Math.round(SOURCE_BRAND_INTRO_SECONDS * doc.fps));
  const data = readSourceBrandIntro({ version: 1, ...text, title: text.title.trim(), width: doc.compositionWidth, height: doc.compositionHeight,
    bg: text.bg ?? SOURCE_BRAND_INTRO_BG, fg: text.fg ?? SOURCE_BRAND_INTRO_FG,
    sourceFps: doc.fps, clock: { startInSeconds: 0, endInSeconds: frames / doc.fps },
    provenance: { kind: 'native', originalStartSec: 0, originalEndSec: frames / doc.fps } })!;
  const affected = ordinaryRoots(state), persistent = persistentRoots(state);
  preflight(state, affected);
  const trackIds = doc.tracks.filter(t => t.metadata?.kind !== 'bgm').map(t => t.id);
  const shifted = insertTimelineTime(state, trackIds, 0, frames, persistent);
  if (!shifted.ok) throw Error('오프닝 카드 앞 시간을 확보하지 못했어요.');
  const extended = resizePersistent(shifted.state, persistent, frames);
  const item = newSourceBrandIntroItem(state, ids, data, 0, frames);
  const next: EditorState = { ...extended, selectedItems: [ids.item], activeSelectionSurface: 'timeline',
    timelineTimeEditApplied: true, undoableState: { ...extended.undoableState,
      items: { ...extended.undoableState.items, [ids.item]: item },
      assets: { ...doc.assets, [ids.asset]: { id: ids.asset, type: 'image', filename: `오프닝 카드 · ${data.title.slice(0,48)}.png`, size: 0,
        mimeType: 'image/png', remoteUrl: SOURCE_BRAND_INTRO_IMAGE, remoteFileKey: null, width: data.width, height: data.height } },
      tracks: [{ id: ids.track, items: [ids.item], zone: 'visual', hidden: false, muted: false, locked: false, magnetic: false,
        metadata: { type: 'image', isMainTrack: false, trackType: null } }, ...doc.tracks] } };
  preflight(next, []);
  return { base: doc, signature: editorValueSignature(doc), action: 'add', next: bindSourceBrandIntroLeadingInterval(next, ids.item) };
}
function deleteCardOnly(state: EditorState, id: string): EditorState {
  const doc = state.undoableState, items = { ...doc.items }, removed = new Set([id]);
  delete items[id];
  // Prune empty membership containers only. Preserve explicitly framed empty groups.
  let changed = true;
  while (changed) {
    changed = false;
    for (const i of Object.values(items)) if (i.type === 'group' && i.children.some(child => removed.has(child))) {
      const children = i.children.filter(child => !removed.has(child));
      if (!children.length && !i.preserveGroupFrame) { removed.add(i.id); delete items[i.id]; changed = true; }
      else items[i.id] = { ...i, children };
    }
  }
  const transitions = Object.fromEntries(Object.entries(doc.transitions).filter(([,t]) => !removed.has(t.sourceItemId) && !removed.has(t.targetItemId)));
  return { ...state, selectedItems: state.selectedItems.filter(key => !removed.has(key)),
    undoableState: { ...doc, items, transitions, tracks: doc.tracks.map(t => ({ ...t, items: t.items.filter(key => !removed.has(key)) })) } };
}
export function planRemoveSourceBrandIntro(state: EditorState, id: string, mode: 'auto' | 'ripple' | 'card-only' = 'auto'): SourceBrandIntroPlan {
  preflight(state, [id]);
  const card = state.undoableState.items[id], data = sourceBrandIntroOfItem(card);
  if (!card || card.type !== 'image' || !data) throw Error('오프닝 카드를 다시 선택해 주세요.');
  const typed: SourceBrandIntroItem = { ...card, sourceBrandIntro: data };
  const r = data.leadingInterval;
  const proven = r && r.card === cardSignature(typed) && r.layout === layoutSignature(state, id) && exclusiveLeading(state, typed, r.frames);
  if (!proven && mode === 'ripple') throw Error('시간 배치가 바뀌었습니다. 카드만 제거해 주세요.');
  let next = deleteCardOnly(state, id);
  if (proven && mode !== 'card-only') {
    preflight(state, ordinaryRoots(state, id));
    const persistent = persistentRoots(state), excluded = new Set(persistent);
    // Native ripple has no per-item exclusions. Detach persistent roots for this pure transaction.
    const detached = { ...next, undoableState: { ...next.undoableState,
      items: Object.fromEntries(Object.entries(next.undoableState.items).filter(([key]) => !excluded.has(key))),
      tracks: next.undoableState.tracks.map(t => ({ ...t, items: t.items.filter(key => !excluded.has(key)) })) } };
    const removed = removeTimelineTime(detached, state.undoableState.tracks.filter(t => t.metadata?.kind !== 'bgm').map(t => t.id), [{start: 0, end: r.frames}]);
    if (!removed.ok) throw Error('오프닝 카드 시간을 제거하지 못했어요.');
    const resized = resizePersistent(next, persistent, -r.frames);
    const restoreIds = new Set<string>();
    const collect = (key: string) => { restoreIds.add(key); const i = resized.undoableState.items[key]; if (i?.type === 'group') i.children.forEach(collect); };
    persistent.forEach(collect);
    next = { ...removed.state, undoableState: { ...removed.state.undoableState,
      items: { ...removed.state.undoableState.items, ...Object.fromEntries([...restoreIds].map(key => [key, resized.undoableState.items[key]])) },
      tracks: removed.state.undoableState.tracks.map(t => ({ ...t, items: next.undoableState.tracks.find(old => old.id === t.id)!.items })) } };
  }
  preflight(next, []);
  const ripple = !!proven && mode !== 'card-only';
  return { base: state.undoableState, signature: editorValueSignature(state.undoableState), action: 'remove', next: { ...next, timelineTimeEditApplied: true },
    removalMode: ripple ? 'ripple' : 'card-only', ...(!ripple ? { note: '카드만 제거합니다. 다른 클립과 영상 길이는 유지합니다.' } : {}) };
}
/** Use within functional native setState({update, commitToUndoStack:true}); reject stale review. */
export function applySourceBrandIntroPlan(current: EditorState, plan: SourceBrandIntroPlan): EditorState {
  if (current.undoableState !== plan.base || editorValueSignature(current.undoableState) !== plan.signature) throw Error('문서가 바뀌었습니다. 오프닝 카드 변경을 다시 확인해 주세요.');
  if (hasPendingTimelineEdit(current)) throw Error('진행 중인 편집을 먼저 마쳐 주세요.');
  return { ...current, undoableState: plan.next.undoableState, selectedItems: plan.action === 'add' ? plan.next.selectedItems : current.selectedItems.filter(id => !!plan.next.undoableState.items[id]),
    activeSelectionSurface: plan.action === 'add' ? 'timeline' : current.activeSelectionSurface,
    timelineTimeEditApplied: plan.next.timelineTimeEditApplied,
    activeGroupId: current.activeGroupId && plan.next.undoableState.items[current.activeGroupId] ? current.activeGroupId : null,
    inspectedGroupMemberId: current.inspectedGroupMemberId && plan.next.undoableState.items[current.inspectedGroupMemberId] ? current.inspectedGroupMemberId : null,
    textItemEditing: current.textItemEditing && plan.next.undoableState.items[current.textItemEditing] ? current.textItemEditing : null,
  };
}
